import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField, IconButton, Stack, Switch, FormControlLabel } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Delete } from '@mui/icons-material';

import FileDropZone from '../../common/components/FileDropZone';
import { useAppDispatch } from 'src/redux-app-hooks';
import { createTender } from './offer-evaluation.slice';
import StepperActions from './components/StepperActions';
import { useOfferEvaluationStepFromUrl, useTenderIdFromUrl } from './hooks';
import { ContactAutocomplete } from '../crm/components/ContactAutocomplete';
import { Contact } from '../crm/crm.dto';

export default function OfferEvaluationStepCreateTender() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [activeStep, setActiveStep] = useOfferEvaluationStepFromUrl();
    const [_, setTenderId] = useTenderIdFromUrl();

    const validationSchema = yup.object({
        projectName: yup.string().required(t('This field is required')),
        costEstimation: yup.mixed<File>().required(t('This field is required')),
        municipalContact: yup.mixed<Contact>().required(t('This field is required')),
        isMultiLot: yup.boolean(),
        lotNumber: yup
            .number()
            .integer()
            .min(1)
            .nullable()
            .when('isMultiLot', {
                is: true,
                then: (schema) => schema.required(t('This field is required')),
            }),
    });

    const formik = useFormik({
        initialValues: {
            projectName: '',
            costEstimation: null as File | null,
            municipalContact: null as Contact | null,
            isMultiLot: false,
            lotNumber: null as number | null,
        },
        validationSchema,
        onSubmit: async (values) => {
            const municipalContact = values.municipalContact
                ? {
                      contactId: values.municipalContact.id,
                      name: values.municipalContact.name,
                      streetAddress: values.municipalContact.streetAddress,
                      zipCodeAndCity: values.municipalContact.zipCodeAndCity,
                      email: values.municipalContact.email,
                      phone: values.municipalContact.phone,
                      jobTitle: values.municipalContact.jobTitle,
                      gender: values.municipalContact.gender,
                      salutation: values.municipalContact.salutation,
                      primaryCompanyId: values.municipalContact.primaryCompanyId,
                      primaryCompanyName: values.municipalContact.primaryCompanyName,
                      mobilePhone: values.municipalContact.mobilePhone,
                  }
                : null;

            const result = await dispatch(
                createTender({
                    dto: {
                        projectName: values.projectName,
                        municipalContact: municipalContact!,
                        lotNumber: values.lotNumber,
                    },
                    costEstimationFile: values.costEstimation!,
                }),
            );

            if (createTender.fulfilled.match(result)) {
                setTenderId(result.payload.id);
                setActiveStep(activeStep + 1);
            }
        },
    });

    return (
        <>
            <Typography variant="h5" gutterBottom>
                {t('Create Tender')}
            </Typography>

            <Stack sx={{ mt: 2, gap: 2 }}>
                <TextField
                    fullWidth
                    label={t('Project name')}
                    name="projectName"
                    value={formik.values.projectName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.projectName && Boolean(formik.errors.projectName)}
                    helperText={formik.touched.projectName && formik.errors.projectName}
                    required
                />
                <Box>
                    <ContactAutocomplete
                        value={formik.values.municipalContact}
                        onSelectContact={(contact: Contact) => {
                            formik.setFieldValue('municipalContact', contact);
                        }}
                    />
                    {formik.touched.municipalContact && formik.errors.municipalContact && (
                        <Typography color="error" variant="body2">
                            {formik.errors.municipalContact}
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {t('The following fields are required for contacts show up here')}: name, address, email, phone
                    </Typography>
                </Box>
                <Stack direction="column" sx={{ gap: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formik.values.isMultiLot}
                                onChange={(e) => {
                                    formik.setFieldValue('isMultiLot', e.target.checked);
                                    if (!e.target.checked) {
                                        formik.setFieldValue('lotNumber', null);
                                    }
                                }}
                            />
                        }
                        label={t('Multi-Lot')}
                    />
                    {formik.values.isMultiLot && (
                        <>
                            <TextField
                                label={t('Lot Number')}
                                name="lotNumber"
                                required
                                sx={{ width: 150 }}
                                value={formik.values.lotNumber ?? ''}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value, 10);
                                    formik.setFieldValue('lotNumber', isNaN(val) ? null : val);
                                }}
                                onBlur={formik.handleBlur}
                                error={formik.touched.lotNumber && Boolean(formik.errors.lotNumber)}
                                helperText={formik.touched.lotNumber && formik.errors.lotNumber}
                            />
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {t(
                                    'The lot number is the first part of the 4-level ordinal number, i.e. <N>.01.01.0010. where <N> is the lot number.',
                                )}
                            </Typography>
                        </>
                    )}
                </Stack>

                <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                    {t('Import Cost Estimation')}
                </Typography>
                {formik.values.costEstimation && (
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            border: '1px solid #ccc',
                            borderRadius: 1,
                            p: 2,
                        }}
                    >
                        <Typography sx={{ flexGrow: 1 }}>{formik.values.costEstimation?.name}</Typography>
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => formik.setFieldValue('costEstimation', undefined)}
                        >
                            <Delete />
                        </IconButton>
                    </Box>
                )}
                {!formik.values.costEstimation && (
                    <FileDropZone
                        onFilesAdd={(files) => formik.setFieldValue('costEstimation', files[0])}
                        acceptedExtensions={['.x82']}
                        maxFiles={1}
                        error={formik.touched.costEstimation && Boolean(formik.errors.costEstimation)}
                        helperText={formik.touched.costEstimation && formik.errors.costEstimation}
                    />
                )}
            </Stack>

            <StepperActions switchPage={false} onNextClick={formik.handleSubmit} />
        </>
    );
}
